import { Link, useNavigate } from "react-router-dom";
import { Menu, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

const gewerbeflaechenLinks = [
  { 
    to: "/lagerhalle-mieten", 
    label: "Lagerhalle mieten",
    description: "Finden Sie die perfekte Lagerhalle für Ihr Unternehmen"
  },
  { 
    to: "/ladenfleache-mieten", 
    label: "Ladenfläche mieten",
    description: "Passende Ladenflächen für Ihr Geschäft finden"
  },
  { 
    to: "/geschaeftsraeume-mieten", 
    label: "Geschäftsräume mieten",
    description: "Geschäftsräume für Ihr Unternehmen mieten"
  },
];

const gewerbegebieteLinks = [
  { 
    to: "/gewerbegebiet-finden", 
    label: "Gewerbegebiet in meiner Nähe",
    description: "Entdecken Sie Gewerbegebiete in Ihrer Umgebung"
  },
];

const cityLinks = [
  { 
    to: "/gewerbeflaeche-mieten-berlin", 
    label: "Berlin",
    description: "Gewerbeflächen in der Hauptstadt"
  },
  { 
    to: "/gewerbeflaeche-mieten-muenchen", 
    label: "München",
    description: "Gewerbeflächen in Bayern"
  },
  { 
    to: "/gewerbeflaeche-mieten-hamburg", 
    label: "Hamburg",
    description: "Gewerbeflächen in der Hansestadt"
  },
  { 
    to: "/gewerbeflaeche-mieten-frankfurt", 
    label: "Frankfurt am Main",
    description: "Gewerbeflächen im Finanzzentrum"
  },
  { 
    to: "/gewerbeflaeche-mieten-koeln", 
    label: "Köln",
    description: "Gewerbeflächen in NRW"
  },
  { 
    to: "/gewerbeflaeche-mieten-duesseldorf", 
    label: "Düsseldorf",
    description: "Gewerbeflächen am Rhein"
  },
  { 
    to: "/stuttgart-gewerbeimmobilien", 
    label: "Stuttgart",
    description: "Gewerbeflächen in Baden-Württemberg"
  },
  { 
    to: "/leipzig-gewerbeimmobilien", 
    label: "Leipzig",
    description: "Gewerbeflächen in Sachsen"
  },
];

const ressourcenLinks = [
  { 
    to: "/portal-finder", 
    label: "Portal-Finder",
    description: "Finden Sie das beste Portal für Ihre Immobiliensuche"
  },
  { 
    to: "/berechnung-von-miete", 
    label: "Berechnung von Miete + Mietpreisrechner",
    description: "Berechnen Sie Ihre Gewerbemiete mit unserem kostenlosen Rechner"
  },
  { 
    to: "/gewerbeversicherungen-vergleich", 
    label: "Gewerbeversicherung Vergleich",
    description: "Vergleichen Sie Gewerbeversicherungen für Ihr Unternehmen"
  },
  { 
    to: "/ressourcen/immowelt-oder-immobilienscout24", 
    label: "Immowelt oder ImmobilienScout24",
    description: "Welches Portal ist das richtige für Ihre Immobiliensuche?"
  },
  { 
    to: "/rezension", 
    label: "Reviews: Portale & Makler",
    description: "Unabhängige Bewertungen von Immobilienportalen und Maklern"
  },
];

const CollapsibleNavSection = ({ title, links, onNavigate }: { 
  title: string; 
  links: { to: string; label: string; description: string }[];
  onNavigate: () => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-t pt-2 mt-2">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between text-sm font-semibold text-foreground px-4 py-2"
      >
        {title}
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && links.map((link) => (
        <Link
          key={link.to}
          to={link.to}
          onClick={onNavigate}
          className="block text-sm py-3 px-4 rounded-lg transition-colors text-muted-foreground hover:bg-muted"
        >
          {link.label}
        </Link>
      ))}
    </div>
  );
};

export const Navigation = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <>
      {/* WCAG 2.4.1 - Skip to main content */}
      <a href="#main-content" className="skip-to-content">
        Zum Hauptinhalt springen
      </a>

      {/* Mobile and Tablet Navigation */}
      <nav className="lg:hidden sticky top-0 z-50 border-b bg-card min-h-[56px]" aria-label="Mobile Navigation">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center" aria-label="Zur Startseite">
            <img src="/favicon.png" alt="Gewerbefläche mieten 2026 – Gewerbefinder Logo" className="h-8 w-8" />
          </Link>
          <Drawer open={open} onOpenChange={setOpen}>
            <DrawerTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Navigationsmenü öffnen">
                <Menu className="h-5 w-5" aria-hidden="true" />
              </Button>
            </DrawerTrigger>
            <DrawerContent className="max-h-[85vh]">
              <DrawerHeader>
                <DrawerTitle>Navigation</DrawerTitle>
              </DrawerHeader>
              <ScrollArea className="flex-1 overflow-auto">
                <div className="flex flex-col gap-2 p-4 pb-8">
                  <DrawerClose asChild>
                    <Link
                      to="/"
                      className="text-base font-medium py-3 px-4 rounded-lg transition-colors text-muted-foreground hover:bg-muted"
                    >
                      Startseite
                    </Link>
                  </DrawerClose>

                  <CollapsibleNavSection title="Gewerbeflächen" links={gewerbeflaechenLinks} onNavigate={() => setOpen(false)} />
                  <CollapsibleNavSection title="Standorte" links={cityLinks} onNavigate={() => setOpen(false)} />
                  <CollapsibleNavSection title="Gewerbegebiete Deutschland" links={[
                    { to: "/gewerbegebiete-deutschland", label: "Übersicht", description: "Alle Gewerbegebiete in Deutschland" },
                    ...gewerbegebieteLinks
                  ]} onNavigate={() => setOpen(false)} />
                  <CollapsibleNavSection title="Ressourcen" links={ressourcenLinks} onNavigate={() => setOpen(false)} />

                  <DrawerClose asChild>
                    <Link
                      to="/ueber-uns"
                      className="text-base font-medium py-3 px-4 rounded-lg transition-colors text-muted-foreground hover:bg-muted"
                    >
                      Über uns
                    </Link>
                  </DrawerClose>
                </div>
              </ScrollArea>
            </DrawerContent>
          </Drawer>
        </div>
      </nav>

      {/* Desktop Navigation */}
      <nav className="hidden lg:block sticky top-0 z-50 border-b bg-card min-h-[56px]" aria-label="Hauptnavigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-8 h-14">
            <Link
              to="/"
              className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
            >
              Startseite
            </Link>
            
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="text-sm font-medium text-muted-foreground hover:text-primary h-auto py-0">
                    Gewerbeflächen
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="bg-background border shadow-lg">
                    <ul className="grid w-[400px] gap-3 p-4">
                      {gewerbeflaechenLinks.map((link) => (
                        <li key={link.to}>
                          <NavigationMenuLink asChild>
                            <Link
                              to={link.to}
                              className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                            >
                              <div className="text-sm font-medium leading-none mb-1">{link.label}</div>
                              <p className="text-xs leading-snug text-muted-foreground">
                                {link.description}
                              </p>
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger className="text-sm font-medium text-muted-foreground hover:text-primary h-auto py-0">
                    Standorte
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="bg-background border shadow-lg">
                    <ul className="grid w-[500px] grid-cols-2 gap-3 p-4">
                      {cityLinks.map((city) => (
                        <li key={city.to}>
                          <NavigationMenuLink asChild>
                            <Link
                              to={city.to}
                              className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                            >
                              <div className="text-sm font-medium leading-none mb-1">{city.label}</div>
                              <p className="text-xs leading-snug text-muted-foreground">
                                {city.description}
                              </p>
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="text-sm font-medium text-muted-foreground hover:text-primary h-auto py-0">
                    Gewerbegebiete Deutschland
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="bg-background border shadow-lg">
                    <ul className="grid w-[400px] gap-3 p-4">
                      <li>
                        <NavigationMenuLink asChild>
                          <Link
                            to="/gewerbegebiete-deutschland"
                            className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                          >
                            <div className="text-sm font-medium leading-none mb-1">Übersicht</div>
                            <p className="text-xs leading-snug text-muted-foreground">
                              Alle Gewerbegebiete in Deutschland
                            </p>
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      {gewerbegebieteLinks.map((link) => (
                        <li key={link.to}>
                          <NavigationMenuLink asChild>
                            <Link
                              to={link.to}
                              className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                            >
                              <div className="text-sm font-medium leading-none mb-1">{link.label}</div>
                              <p className="text-xs leading-snug text-muted-foreground">
                                {link.description}
                              </p>
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger
                    className="text-sm font-medium text-muted-foreground hover:text-primary h-auto py-0"
                    onClick={() => navigate("/ressourcen")}
                  >
                    Ressourcen
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="bg-background border shadow-lg">
                    <ul className="grid w-[450px] gap-3 p-4">
                      {ressourcenLinks.map((link) => (
                        <li key={link.to}>
                          <NavigationMenuLink asChild>
                            <Link
                              to={link.to}
                              className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                            >
                              <div className="text-sm font-medium leading-none mb-1">{link.label}</div>
                              <p className="text-xs leading-snug text-muted-foreground">
                                {link.description}
                              </p>
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                      <li className="border-t pt-3">
                        <NavigationMenuLink asChild>
                          <Link
                            to="/ressourcen"
                            className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground font-semibold text-primary"
                          >
                            <div className="text-sm leading-none">Alle Ressourcen ansehen →</div>
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            <Link
              to="/ueber-uns"
              className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
            >
              Über uns
            </Link>
          </div>
        </div>
      </nav>
    </>
  );
};
